import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import PlayfulNavbar from "../components/playful/PlayfulNavbar";
import PlayfulFooter from "../components/playful/PlayfulFooter";
import { CandyButton } from "../components/playful/CandyButton";
import { PopInput } from "../components/playful/PopInput";
import { PasswordInput } from "../components/playful/PasswordInput";
import { PasswordStrength } from "../components/playful/PasswordStrength";
import { StickerCard } from "../components/playful/StickerCard";
import { GeometricBadge } from "../components/playful/GeometricBadge";
import { FloatingIcon } from "../components/playful/FloatingIcon";
import { Blob, ConfettiDecoration, StarBurst } from "../components/playful/Decor";
import Turnstile, { TURNSTILE_ENABLED } from "../components/playful/Turnstile";
import { GoogleIcon, AppleIcon } from "../components/playful/SocialIcons";
import { useAuth } from "../context/AuthContext";
import { formatApiError, API_BASE } from "../lib/api";
import { readCheckoutIntent } from "../lib/checkoutIntent";

const APPLE_ENABLED = process.env.REACT_APP_OAUTH_APPLE_ENABLED === "true";

// Full-page redirect to the backend OAuth start; backend sends new users to
// /onboarding. Carries the stored referral code for attribution.
function startOauth(provider) {
  const origin = API_BASE.replace(/\/api$/, "");
  const ref = localStorage.getItem("linklane_ref") || "";
  const qs = new URLSearchParams();
  if (ref) qs.set("ref_code", ref);
  window.location.href = `${origin}/api/auth/oauth/${provider}/start?${qs}`;
}

export default function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const checkoutIntent = readCheckoutIntent();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState("");

  const validate = () => {
    const e = {};
    if (password.length < 6) e.password = "At least 6 characters required";
    if (password !== confirm) e.confirm = "Passwords do not match";
    if (!agreed) e.agreed = "Please accept the Terms and Privacy Policy to continue";
    if (TURNSTILE_ENABLED && !captchaToken) e.captcha = "Please complete the bot-protection check";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    setErrors({});
    try {
      await register(email.trim().toLowerCase(), password, name.trim(), captchaToken);
      toast.success("Account created! Let's get you started.");
      navigate("/onboarding");
    } catch (err) {
      const msg = formatApiError(err);
      if (msg.toLowerCase().includes("already")) {
        setErrors({ email: "This email is already registered" });
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper text-ink">
      <PlayfulNavbar />
      <section className="relative mx-auto max-w-[1400px] px-5 pb-20 pt-10 md:px-10 md:pb-32 md:pt-16">
        <Blob
          tone="mint"
          size={360}
          className="absolute -right-28 -top-28 hidden animate-drift opacity-70 md:block"
        />
        <Blob
          tone="candy"
          size={220}
          className="absolute -left-10 top-40 hidden animate-float opacity-70 md:block"
        />
        <StarBurst
          tone="yellow"
          size={90}
          className="absolute right-1/3 top-8 hidden animate-spin-slow md:block"
        />

        <div className="relative grid grid-cols-1 items-center gap-8 md:grid-cols-12 md:gap-10">
          <div className="order-2 md:order-1 md:col-span-6 md:pr-10">
            <GeometricBadge tone="pink">LINKLANE — CREATE ACCOUNT</GeometricBadge>
            {checkoutIntent && (
              <div
                data-testid="checkout-intent-hint"
                className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-ink bg-mint/30 px-4 py-1.5 font-sans text-sm font-semibold shadow-sticker-xs"
              >
                ✓ Create your account to continue to{" "}
                {checkoutIntent.plan === "growth" ? "Growth" : "Pro"} checkout
              </div>
            )}
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-5xl md:text-7xl md:leading-[0.95]">
              Claim your lane.
              <span className="block text-candy">It's free. Always will be.</span>
            </h1>
            <p className="mt-5 max-w-md font-sans text-base md:mt-6 md:text-lg">
              Custom aliases, QR codes, and a dashboard that actually sparks joy. No credit card, no
              confetti tax.
            </p>
            <div className="mt-8 flex gap-4">
              <FloatingIcon icon={UserPlus} tone="candy" size={60} rotate={-6} />
              <FloatingIcon icon={UserPlus} tone="pink" size={44} rotate={10} />
            </div>
          </div>
          <div className="order-1 min-w-0 md:order-2 md:col-span-5 md:col-start-8">
            <StickerCard
              tone="pink"
              bg="paper"
              hover={false}
              className="relative p-6 sm:p-8 md:p-10"
            >
              <ConfettiDecoration className="opacity-30" density="sparse" />
              <form
                onSubmit={onSubmit}
                className="relative flex flex-col gap-5"
                data-testid="signup-form"
              >
                <PopInput
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Driver in Chief"
                  autoFocus
                  data-testid="signup-name-input"
                />
                <PopInput
                  type="email"
                  required
                  label="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors((p) => ({ ...p, email: undefined }));
                  }}
                  placeholder="you@domain.com"
                  error={errors.email}
                  data-testid="signup-email-input"
                />
                <div>
                  <PasswordInput
                    required
                    label="Password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrors((p) => ({ ...p, password: undefined }));
                    }}
                    placeholder="At least 6 characters"
                    error={errors.password}
                    data-testid="signup-password-input"
                  />
                  <PasswordStrength password={password} />
                </div>
                <PasswordInput
                  required
                  label="Confirm password"
                  value={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.value);
                    setErrors((p) => ({ ...p, confirm: undefined }));
                  }}
                  placeholder="Repeat your password"
                  error={errors.confirm}
                  data-testid="signup-confirm-input"
                />
                <label
                  className="flex items-start gap-2.5 font-sans text-sm"
                  data-testid="signup-tos"
                >
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => {
                      setAgreed(e.target.checked);
                      setErrors((p) => ({ ...p, agreed: undefined }));
                    }}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-candy"
                    data-testid="signup-tos-checkbox"
                  />
                  <span>
                    I agree to LinkLane's{" "}
                    <Link
                      to="/terms"
                      target="_blank"
                      className="text-candy underline-offset-2 hover:underline"
                    >
                      Terms
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy"
                      target="_blank"
                      className="text-candy underline-offset-2 hover:underline"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>
                {errors.agreed && (
                  <p
                    className="-mt-2 font-sans text-sm text-red-500"
                    data-testid="signup-tos-error"
                  >
                    {errors.agreed}
                  </p>
                )}
                <Turnstile onToken={setCaptchaToken} />
                {errors.captcha && (
                  <p
                    className="-mt-2 font-sans text-sm text-red-500"
                    data-testid="signup-captcha-error"
                  >
                    {errors.captcha}
                  </p>
                )}
                <CandyButton
                  type="submit"
                  variant="candy"
                  size="lg"
                  block
                  disabled={busy || !agreed}
                  withArrow
                  data-testid="signup-submit-btn"
                >
                  {busy ? "Creating account…" : "Create account"}
                </CandyButton>
                <div className="flex items-center gap-3">
                  <span className="h-px flex-1 bg-line" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                    or
                  </span>
                  <span className="h-px flex-1 bg-line" />
                </div>
                <CandyButton
                  type="button"
                  variant="paper"
                  size="lg"
                  block
                  icon={GoogleIcon}
                  onClick={() => startOauth("google")}
                  data-testid="signup-google-btn"
                >
                  Continue with Google
                </CandyButton>
                {APPLE_ENABLED && (
                  <CandyButton
                    type="button"
                    variant="ink"
                    size="lg"
                    block
                    icon={AppleIcon}
                    onClick={() => startOauth("apple")}
                    data-testid="signup-apple-btn"
                  >
                    Continue with Apple
                  </CandyButton>
                )}
                <p className="text-center font-sans text-xs text-muted-foreground">
                  By continuing with Google or Apple you agree to LinkLane's{" "}
                  <Link to="/terms" target="_blank" className="text-candy hover:underline">
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" className="text-candy hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
                <div className="text-center font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  ALREADY ON LINKLANE?{" "}
                  <Link
                    to="/login"
                    className="text-candy underline-offset-4 hover:underline"
                    data-testid="signup-go-login"
                  >
                    SIGN IN
                  </Link>
                </div>
              </form>
            </StickerCard>
          </div>
        </div>
      </section>
      <PlayfulFooter />
    </div>
  );
}
